import React from "react";
import { Phone, Mail, MapPin, ArrowRight, Calendar, Stethoscope } from "lucide-react";
import { SITE, HERO_QUICK_LINKS } from "@/lib/site-data";

const ICONS = {
    services: Stethoscope,
    rdv: Calendar,
    contact: Phone,
};

export const Hero = () => {
    return (
        <section
            id="accueil"
            className="relative min-h-screen pt-32 lg:pt-40 pb-24 lg:pb-32 bg-white overflow-hidden"
            data-testid="hero-section"
        >
            {/* Subtle decorative mint blob, very low opacity to keep white identity */}
            <div
                aria-hidden
                className="absolute -top-32 -right-32 w-[640px] h-[640px] rounded-full bg-brand-mint/25 blur-3xl pointer-events-none"
            />
            <div
                aria-hidden
                className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-brand-mint/20 blur-3xl pointer-events-none"
            />

            <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10 text-center flex flex-col items-center">
                {/* Big logo — main hero element */}
                <img
                    src="/brand/logo-900.webp"
                    srcSet="/brand/logo-600.webp 600w, /brand/logo-900.webp 900w"
                    sizes="(min-width: 1024px) 430px, 320px"
                    width="900"
                    height="599"
                    fetchPriority="high"
                    alt="Pharmacie La Lutrive"
                    className="mx-auto w-auto h-40 sm:h-48 md:h-56 lg:h-64 xl:h-72 select-none"
                    draggable={false}
                    data-testid="hero-logo"
                />

                {/* Title */}
                <h1
                    className="mt-10 lg:mt-12 font-['Lato',sans-serif] font-normal text-xl sm:text-3xl lg:text-4xl tracking-[0.08em] text-[#2f5c5e] uppercase"
                    data-testid="hero-title"
                >
                    {SITE.tagline}
                </h1>

                {/* Presentation — short two paragraphs */}
                <div className="mt-10 lg:mt-12 max-w-xl space-y-5 font-sans text-base lg:text-lg leading-[1.8] text-brand-ink/60">
                    {SITE.presentation.map((p, i) => (
                        <p key={i}>{p}</p>
                    ))}
                </div>

                {/* Quick action buttons */}
                <div className="mt-12 lg:mt-14 flex flex-wrap items-center justify-center gap-4" data-testid="hero-actions">
                    <a
                        href="/rendez-vous"
                        className="ll-btn ll-btn-primary"
                        data-testid="hero-rdv-btn"
                    >
                        <Calendar className="h-4 w-4" />
                        Prendre rendez-vous
                    </a>
                    <a
                        href="#services"
                        className="ll-btn ll-btn-ghost"
                        data-testid="hero-services-btn"
                    >
                        Nos prestations
                        <ArrowRight className="h-4 w-4" />
                    </a>
                    <a
                        href={`tel:${SITE.phone}`}
                        className="ll-btn ll-btn-ghost"
                        data-testid="hero-phone-btn"
                    >
                        <Phone className="h-4 w-4" />
                        {SITE.phoneDisplay}
                    </a>
                </div>

                {/* Key info row — address / phone / email */}
                <div className="mt-16 lg:mt-24 grid grid-cols-1 sm:grid-cols-3 gap-5 lg:gap-6 w-full max-w-3xl text-left">
                    <InfoCard
                        icon={<MapPin className="h-4 w-4" />}
                        eyebrow="Adresse"
                        main={`${SITE.address.line1}`}
                        sub={`${SITE.address.zip} ${SITE.address.city}`}
                        href={SITE.mapsLink}
                        testId="hero-info-address"
                    />
                    <InfoCard
                        icon={<Phone className="h-4 w-4" />}
                        eyebrow="Téléphone"
                        main={SITE.phoneDisplay}
                        sub={<>Lun–Ven · 08h – 18h30<br />Sam · 08h – 17h</>}
                        href={`tel:${SITE.phone}`}
                        testId="hero-info-phone"
                    />
                    <InfoCard
                        icon={<Mail className="h-4 w-4" />}
                        eyebrow="Courriel"
                        main={SITE.email}
                        sub="Réponse sous 24h"
                        href={`mailto:${SITE.email}`}
                        testId="hero-info-email"
                    />
                </div>

                {/* Quick links row */}
                <div className="hidden lg:flex mt-16 items-center gap-16 text-sm text-brand-ink/55 font-sans">
                    {HERO_QUICK_LINKS.map((q) => {
                        const Icon = ICONS[q.key] || Stethoscope;
                        return (
                            <a
                                key={q.key}
                                href={q.href}
                                className="group inline-flex items-center gap-3 hover:text-brand-primary transition-colors"
                                data-testid={`hero-quick-${q.key}`}
                            >
                                <Icon className="h-4 w-4 text-brand-primary" />
                                <span>
                                    <span className="block text-brand-ink/85 font-medium">{q.label}</span>
                                    <span className="block text-xs text-brand-ink/45">{q.sub}</span>
                                </span>
                            </a>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

const InfoCard = ({ icon, eyebrow, main, sub, href, testId }) => (
    <a
        href={href}
        className="group relative bg-white/70 border border-brand-line/60 hover:border-brand-primary/50 transition-all duration-300 p-6 flex items-start gap-3 text-left hover:-translate-y-0.5"
        data-testid={testId}
    >
        <span className="h-9 w-9 rounded-full bg-brand-mint/40 text-brand-primary inline-flex items-center justify-center shrink-0 group-hover:bg-brand-primary group-hover:text-white transition-colors">
            {icon}
        </span>
        <div className="min-w-0">
            <p className="ll-eyebrow text-brand-ink/50 mb-1">{eyebrow}</p>
            <p className="font-sans text-sm text-brand-ink truncate">{main}</p>
            <p className="font-sans text-xs text-brand-ink/55 mt-0.5">{sub}</p>
        </div>
    </a>
);

export default Hero;
